/**
 * Print Document List — Unified Print Hub
 *
 * Shows all printable documents linked to a Sales Order in a dropdown menu.
 * The SO itself + Sales Invoices, Delivery Notes, Payment Entries, Pick Lists.
 * Each item navigates to the full print preview page.
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Printer, FileText, Loader2, Sparkles } from "lucide-react";
import {
  getPrintableDocuments,
  type LinkedDocument,
} from "@/lib/print/printService";

type PrintDocumentListProps = {
  salesOrder: string;
};

/** Concise icon per document type */
function DocIcon({ doctype }: { doctype: string }) {
  // All use FileText — keeping the mapping extensible if we want
  // different icons per type later
  void doctype;
  return <FileText className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" />;
}

export function PrintDocumentList({ salesOrder }: PrintDocumentListProps) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["printable-documents", salesOrder],
    queryFn: () => getPrintableDocuments(salesOrder),
    enabled: !!salesOrder,
    staleTime: 30_000, // 30s — linked docs don't change often
  });

  const openPrint = (doctype: string, name: string, printFormat?: string) => {
    const base = `/print/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}`;
    navigate(
      printFormat
        ? `${base}?format=${encodeURIComponent(printFormat)}`
        : base
    );
  };

  const openPrintStudio = (doctype: string, name: string) => {
    navigate(
      `/print-studio/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}`
    );
  };

  const keyDocs = data?.key_documents ?? [];
  const hasKeyDocs = keyDocs.length > 0;
  const linkedDocs = data?.linked ?? [];
  const hasLinked = linkedDocs.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="shrink-0">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Printer className="w-4 h-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* Sales Order itself */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Print Documents
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => openPrintStudio("Sales Order", salesOrder)}
        >
          <Sparkles className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" />
          <div className="flex flex-col min-w-0">
            <span className="truncate font-medium">Print Studio</span>
            <span className="text-xs text-muted-foreground truncate">
              {salesOrder} — React templates
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openPrint("Sales Order", salesOrder)}
        >
          <DocIcon doctype="Sales Order" />
          <div className="flex flex-col min-w-0">
            <span className="truncate font-medium">Classic Print</span>
            <span className="text-xs text-muted-foreground truncate">
              {salesOrder} — Jinja templates
            </span>
          </div>
        </DropdownMenuItem>

        {/* Key documents — Tax Invoice / Quotation / Receipt (one click) */}
        {hasKeyDocs && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              เอกสาร / Documents
            </DropdownMenuLabel>
            {keyDocs.map((doc: LinkedDocument) => (
              <DropdownMenuItem
                key={`key-${doc.doctype}-${doc.name}-${doc.print_format ?? ""}`}
                onClick={() => openPrint(doc.doctype, doc.name, doc.print_format)}
              >
                <DocIcon doctype={doc.doctype} />
                <div className="flex flex-col min-w-0">
                  <span className="truncate font-medium">{doc.label}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {doc.name}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}

        {/* Linked documents */}
        {hasLinked && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Related Documents
            </DropdownMenuLabel>
            {linkedDocs.map((doc: LinkedDocument) => (
              <DropdownMenuItem
                key={`${doc.doctype}-${doc.name}`}
                onClick={() => openPrint(doc.doctype, doc.name)}
              >
                <DocIcon doctype={doc.doctype} />
                <div className="flex flex-col min-w-0">
                  <span className="truncate font-medium">{doc.label}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {doc.name}
                    {doc.status ? ` · ${doc.status}` : ""}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}

        {/* Loading state */}
        {isLoading && (
          <DropdownMenuItem disabled>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading documents...
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
